import { MutableRefObject } from 'react';
import { FlashList } from '@shopify/flash-list';
import {
  DIFF_ROW_DOM_ID_PREFIX,
  JUMP_VIEW_POSITION,
  WEB_SCROLL_MAX_ATTEMPTS,
  WEB_SCROLL_POLL_INTERVAL_MS,
} from '../constants/diffView.constants';
import { isWeb } from '@helpers';
import { ChangeSummaryEntry, DiffListItem } from '@components/ncFileVersions/components/NCFileDiffView/types';
import { flashRowHighlight } from './flashRowHighlight';

type HighlightState = { key: string; tick: number } | null;

export interface PerformJumpToChangeContext {
  list: FlashList<DiffListItem> | null;
  preparedListItems: DiffListItem[];
  cancelPendingWebJump: () => void;
  webScrollTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  webHighlightCancelRef: MutableRefObject<(() => void) | null>;
  setHighlightState: (updater: (prev: HighlightState) => HighlightState) => void;
}

export const performJumpToChange = (entry: ChangeSummaryEntry, ctx: PerformJumpToChangeContext): void => {
  const { list, preparedListItems, cancelPendingWebJump, webScrollTimerRef, webHighlightCancelRef, setHighlightState } = ctx;
  if (!list) {
    return;
  }
  // Trust the cached rowIndex when the row at that slot still matches; only fall back
  // to the O(n) scan when section expansion has shifted indices since classification.
  const cachedIndex = entry.rowIndex;
  const currentIndex = preparedListItems[cachedIndex]?.key === entry.rowKey
    ? cachedIndex
    : preparedListItems.findIndex((item) => item.key === entry.rowKey);
  if (currentIndex < 0) {
    return;
  }

  // Tablet: native UIScrollView always honors this. Web: it's a no-op when the FlashList
  // host div isn't actually the scrolling element, but it still updates FlashList's
  // engaged-indices so the target row gets rendered into the DOM for the fallback.
  list.scrollToIndex({ index: currentIndex, animated: true, viewPosition: JUMP_VIEW_POSITION });

  if (isWeb) {
    // Poll briefly — FlashList may need a frame or two to mount the target row
    // before scrollIntoView has a DOM node to walk up from.
    cancelPendingWebJump();
    let attempts = 0;
    const tryScroll = () => {
      webScrollTimerRef.current = null;
      const target = document.getElementById(`${DIFF_ROW_DOM_ID_PREFIX}${entry.rowKey}`);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (target.parentElement) {
          webHighlightCancelRef.current = flashRowHighlight(target.parentElement);
        }
        return;
      }
      if (attempts++ < WEB_SCROLL_MAX_ATTEMPTS) {
        webScrollTimerRef.current = setTimeout(tryScroll, WEB_SCROLL_POLL_INTERVAL_MS);
      }
    };
    tryScroll();
  } else {
    setHighlightState((prev) => ({ key: entry.rowKey, tick: (prev?.tick ?? 0) + 1 }));
  }
};
